<template>
  <div>
    <!-- Фильтры -->
    <b-card no-body>
      <b-card-header class="pb-50">
        <h5>Фильтры</h5>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="3" class="mb-md-0 mb-1">
            <label>Дата начала</label>
            <flat-pickr
              v-model="dateFrom"
              class="form-control"
              :config="datePickerConfig"
              placeholder="Выберите дату"
            />
          </b-col>
          <b-col cols="12" md="3" class="mb-md-0 mb-1">
            <label>Дата конца</label>
            <flat-pickr
              v-model="dateTo"
              class="form-control"
              :config="datePickerConfig"
              placeholder="Выберите дату"
            />
          </b-col>
          <b-col cols="12" md="2" class="mb-md-0 mb-1">
            <label>Ведущий</label>
            <v-select
              v-model="hostFilter"
              :options="hostOptions"
              :clearable="true"
              placeholder="Все"
              class="w-100"
            />
          </b-col>
          <b-col cols="12" md="2" class="mb-md-0 mb-1">
            <label>Пакет</label>
            <v-select
              v-model="packFilter"
              :options="packOptions"
              :clearable="true"
              placeholder="Все"
              class="w-100"
            />
          </b-col>
          <b-col cols="12" md="2" class="mb-md-0 mb-1">
            <label>Автор пакета</label>
            <v-select
              v-model="authorFilter"
              :options="authorOptions"
              :clearable="true"
              placeholder="Все"
              class="w-100"
            />
          </b-col>
        </b-row>
        <b-row class="mt-1">
          <b-col cols="12" class="d-flex justify-content-end">
            <b-button variant="outline-secondary" class="mr-1" @click="resetFilters">
              Сбросить
            </b-button>
            <b-button variant="primary" @click="applyFilters">
              Применить
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <b-overlay :show="isLoading" rounded="sm">
      <!-- Графики трендов по неделям -->
      <b-card v-if="weeklyData.length" title="Тренды оценок по неделям">
        <vue-apex-charts
          type="line"
          height="350"
          :options="weeklyChartOptions"
          :series="weeklySeries"
        />
      </b-card>

      <b-row>
        <!-- Рейтинг ведущих -->
        <b-col cols="12" md="6">
          <b-card v-if="hostData.length" title="Рейтинг ведущих">
            <vue-apex-charts
              type="bar"
              height="300"
              :options="hostChartOptions"
              :series="hostSeries"
            />
          </b-card>
        </b-col>

        <!-- Рейтинг авторов пакетов -->
        <b-col cols="12" md="6">
          <b-card v-if="authorData.length" title="Рейтинг авторов пакетов">
            <vue-apex-charts
              type="bar"
              height="300"
              :options="authorChartOptions"
              :series="authorSeries"
            />
          </b-card>
        </b-col>
      </b-row>

      <!-- Рейтинг пакетов -->
      <b-card v-if="packData.length" title="Рейтинг пакетов">
        <vue-apex-charts
          type="bar"
          height="1200"
          :options="packChartOptions"
          :series="packSeries"
        />
      </b-card>

      <!-- Таблица отзывов -->
      <b-card no-body>
        <b-card-header class="d-flex justify-content-between align-items-center">
          <h4 class="mb-0">Отзывы игроков</h4>
          <b-button variant="outline-primary" size="sm" @click="copyComments">
            <feather-icon icon="CopyIcon" class="mr-50" />
            Копировать комментарии
          </b-button>
        </b-card-header>
        <b-card-body>
        <vue-good-table
          :columns="columns"
          :rows="filteredRows"
          :search-options="{ enabled: true, externalQuery: searchTerm }"
          :pagination-options="{ enabled: true, perPage: 20 }"
        >
          <template slot="table-row" slot-scope="props">
            <span v-if="props.column.field === 'host'">
              <b-link v-if="props.row.host" class="text-primary" @click="hostFilter = props.row.host">
                {{ props.row.host }}
              </b-link>
              <span v-else class="text-muted">—</span>
            </span>
            <span v-else-if="props.column.field === 'questionPack'">
              <b-link v-if="props.row.questionPack" class="text-primary" @click="packFilter = props.row.questionPack">
                {{ props.row.questionPack }}
              </b-link>
              <span v-else class="text-muted">—</span>
            </span>
            <span v-else-if="props.column.field === 'packAuthor'">
              <b-link v-if="props.row.packAuthor" class="text-primary" @click="authorFilter = props.row.packAuthor">
                {{ props.row.packAuthor }}
              </b-link>
              <span v-else class="text-muted">—</span>
            </span>
            <span v-else-if="props.column.field === 'gameRating'">
              {{ props.row.gameRating != null ? props.row.gameRating : '—' }}
            </span>
            <span v-else-if="props.column.field === 'hostRating'">
              {{ props.row.hostRating != null ? props.row.hostRating : '—' }}
            </span>
            <span v-else-if="props.column.field === 'packRating'">
              {{ props.row.packRating != null ? props.row.packRating : '—' }}
            </span>
            <span v-else-if="props.column.field === 'comment'">
              <span v-if="props.row.comment" v-b-tooltip.hover :title="props.row.comment">
                {{ truncate(props.row.comment, 80) }}
              </span>
              <span v-else class="text-muted">—</span>
            </span>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>

          <template slot="pagination-bottom" slot-scope="props">
            <div class="d-flex justify-content-between flex-wrap">
              <div class="d-flex align-items-center mb-0 mt-1">
                <span class="text-muted mr-1">Всего {{ props.total }} записей</span>
              </div>
              <div>
                <b-pagination
                  :value="currentPage"
                  :total-rows="props.total"
                  :per-page="20"
                  first-number
                  last-number
                  prev-class="prev-item"
                  next-class="next-item"
                  class="mt-1 mb-0"
                  @input="(value) => { currentPage = value; props.pageChanged({ currentPage: value }) }"
                >
                  <template #prev-text>
                    <feather-icon icon="ChevronLeftIcon" size="18" />
                  </template>
                  <template #next-text>
                    <feather-icon icon="ChevronRightIcon" size="18" />
                  </template>
                </b-pagination>
              </div>
            </div>
          </template>
        </vue-good-table>

        <!-- Средние оценки -->
        <div v-if="filteredRows.length" class="mt-2 pt-1 border-top">
          <b-row>
            <b-col cols="4" class="text-center">
              <small class="text-muted d-block">Средняя оценка игры</small>
              <h4 class="mb-0">{{ averages.game }}</h4>
            </b-col>
            <b-col cols="4" class="text-center">
              <small class="text-muted d-block">Средняя оценка ведущего</small>
              <h4 class="mb-0">{{ averages.host }}</h4>
            </b-col>
            <b-col cols="4" class="text-center">
              <small class="text-muted d-block">Средняя оценка пакета</small>
              <h4 class="mb-0">{{ averages.pack }}</h4>
            </b-col>
          </b-row>
        </div>
        </b-card-body>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, BRow, BCol, BButton, BLink,
  BPagination, BOverlay, VBTooltip,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import VueApexCharts from 'vue-apexcharts'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import store from '@/store'
import feedbackStoreModule from '@/views/feedback/feedbackStoreModule'
import { ref, computed, onUnmounted } from '@vue/composition-api'
import { $themeColors } from '@themeConfig'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BLink,
    BPagination,
    BOverlay,
    VueGoodTable,
    VueApexCharts,
    vSelect,
    flatPickr,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  setup() {
    const STORE_MODULE_NAME = 'app-feedback'

    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, feedbackStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    // Фильтры
    const dateFrom = ref('')
    const dateTo = ref('')
    const appliedDateFrom = ref('')
    const appliedDateTo = ref('')
    const hostFilter = ref(null)
    const packFilter = ref(null)
    const authorFilter = ref(null)
    const appliedHostFilter = ref(null)
    const appliedPackFilter = ref(null)
    const appliedAuthorFilter = ref(null)
    const searchTerm = ref('')
    const currentPage = ref(1)
    const isLoading = ref(false)

    const datePickerConfig = {
      dateFormat: 'Y-m-d',
      altInput: true,
      altFormat: 'd.m.Y',
    }

    // Данные
    const rows = ref([])
    const weeklyData = ref([])
    const hostData = ref([])
    const packData = ref([])
    const authorData = ref([])

    // Опции для фильтров (извлекаются из данных)
    const hostOptions = computed(() => {
      const hosts = [...new Set(rows.value.map(r => r.host).filter(Boolean))]
      return hosts.sort()
    })
    const packOptions = computed(() => {
      const packs = [...new Set(rows.value.map(r => r.questionPack).filter(Boolean))]
      return packs.sort()
    })
    const authorOptions = computed(() => {
      const authors = [...new Set(rows.value.map(r => r.packAuthor).filter(Boolean))]
      return authors.sort()
    })

    // Фильтрация строк на клиенте (по применённым фильтрам)
    const filteredRows = computed(() => {
      let result = rows.value
      if (appliedHostFilter.value) {
        result = result.filter(r => r.host === appliedHostFilter.value)
      }
      if (appliedPackFilter.value) {
        result = result.filter(r => r.questionPack === appliedPackFilter.value)
      }
      if (appliedAuthorFilter.value) {
        result = result.filter(r => r.packAuthor === appliedAuthorFilter.value)
      }
      return result
    })

    // Колонки таблицы
    const columns = [
      { label: 'Игра', field: 'gameName' },
      { label: 'Ведущий', field: 'host' },
      { label: 'Дата', field: 'commentDate' },
      { label: 'Игрок', field: 'playerName' },
      { label: 'Игра', field: 'gameRating', type: 'number', width: '70px' },
      { label: 'Ведущий', field: 'hostRating', type: 'number', width: '85px' },

      { label: 'Пакет', field: 'packRating', type: 'number', width: '75px' },
      { label: 'Пакет вопросов', field: 'questionPack' },
      { label: 'Автор', field: 'packAuthor' },
      { label: 'Комментарий', field: 'comment' },
    ]

    // --- Графики ---

    // Тренды по неделям
    const weeklySeries = computed(() => [
      {
        name: 'Игра',
        data: [...weeklyData.value].reverse().map(w => w.averageGameRating),
      },
      {
        name: 'Ведущий',
        data: [...weeklyData.value].reverse().map(w => w.averageHostRating),
      },
      {
        name: 'Пакет',
        data: [...weeklyData.value].reverse().map(w => w.averagePackRating),
      },
    ])

    const weeklyChartOptions = computed(() => ({
      chart: {
        toolbar: { show: true },
        zoom: { enabled: true },
      },
      colors: [$themeColors.primary, $themeColors.success, $themeColors.info],
      stroke: { curve: 'smooth', width: 2 },
      markers: { size: 4 },
      xaxis: {
        categories: [...weeklyData.value].reverse().map(w => {
          const d = new Date(w.weekFriday)
          return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`
        }),
        title: { text: 'Неделя (пятница)' },
      },
      yaxis: {
        min: 1,
        max: 5,
        title: { text: 'Средняя оценка' },
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter(val) {
            return val != null ? val.toFixed(2) : '—'
          },
        },
      },
      legend: { position: 'top' },
    }))

    // Рейтинг ведущих
    const hostSeries = computed(() => [{
      name: 'Средняя оценка',
      data: hostData.value.map(h => h.averageRating),
    }])

    const hostChartOptions = computed(() => ({
      chart: { toolbar: { show: false } },
      colors: [$themeColors.success],
      plotOptions: {
        bar: { horizontal: true, barHeight: '60%', endingShape: 'rounded' },
      },
      xaxis: { min: 1, max: 5, title: { text: 'Средняя оценка' } },
      yaxis: {
        labels: { style: { fontSize: '12px' } },
      },
      labels: hostData.value.map(h => h.host),
      tooltip: {
        y: {
          formatter(val, { dataPointIndex }) {
            const item = hostData.value[dataPointIndex]
            return `${val != null ? val.toFixed(2) : '—'} (${item ? item.ratingsCount : 0} оценок)`
          },
        },
      },
    }))

    // Рейтинг пакетов
    const packSeries = computed(() => [{
      name: 'Средняя оценка',
      data: packData.value.map(p => p.averageRating),
    }])

    const formatPremiereDate = premiereDate => {
      if (!premiereDate) return ''
      const d = new Date(premiereDate)
      if (Number.isNaN(d.getTime())) return ''
      const dd = String(d.getDate()).padStart(2, '0')
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      return `${dd}.${mm}.${d.getFullYear()}`
    }

    const packChartOptions = computed(() => ({
      chart: { toolbar: { show: false } },
      colors: [$themeColors.info],
      plotOptions: {
        bar: { horizontal: true, barHeight: '60%', endingShape: 'rounded' },
      },
      xaxis: { min: 1, max: 5, title: { text: 'Средняя оценка' } },
      yaxis: {
        labels: {
          style: { fontSize: '12px' },
          maxWidth: 400,
        },
      },
      labels: packData.value.map(p => {
        const premiere = formatPremiereDate(p.premiereDate)
        return premiere ? `${p.pack} (${premiere})` : p.pack
      }),
      tooltip: {
        y: {
          formatter(val, { dataPointIndex }) {
            const item = packData.value[dataPointIndex]
            const count = item ? item.ratingsCount : 0
            const premiere = item ? formatPremiereDate(item.premiereDate) : ''
            const suffix = premiere
              ? `${count} оценок, премьера ${premiere}`
              : `${count} оценок`
            return `${val != null ? val.toFixed(2) : '—'} (${suffix})`
          },
        },
      },
    }))

    // Рейтинг авторов пакетов
    const authorSeries = computed(() => [{
      name: 'Средняя оценка',
      data: authorData.value.map(a => a.averagePackRating),
    }])

    const authorChartOptions = computed(() => ({
      chart: { toolbar: { show: false } },
      colors: [$themeColors.warning],
      plotOptions: {
        bar: { horizontal: true, barHeight: '60%', endingShape: 'rounded' },
      },
      xaxis: { min: 1, max: 5, title: { text: 'Средняя оценка' } },
      yaxis: {
        labels: { style: { fontSize: '12px' } },
      },
      labels: authorData.value.map(a => a.packAuthor),
      tooltip: {
        y: {
          formatter(val, { dataPointIndex }) {
            const item = authorData.value[dataPointIndex]
            return `${val != null ? val.toFixed(2) : '—'} (${item ? item.ratingsCount : 0} оценок)`
          },
        },
      },
    }))

    // Загрузка данных
    const fetchData = () => {
      isLoading.value = true
      appliedDateFrom.value = dateFrom.value
      appliedDateTo.value = dateTo.value
      const params = {}
      if (dateFrom.value) params.date_from = dateFrom.value
      if (dateTo.value) params.date_to = dateTo.value

      store.dispatch('app-feedback/fetchFeedback', params)
        .then(response => {
          const data = response.data.data
          rows.value = data.rows || []
          weeklyData.value = data.aggregations.byGameWeek || []
          hostData.value = data.aggregations.byHost || []
          packData.value = data.aggregations.byPack || []
          authorData.value = data.aggregations.byPackAuthor || []
        })
        .catch(() => {
          rows.value = []
          weeklyData.value = []
          hostData.value = []
          packData.value = []
          authorData.value = []
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    const applyFilters = () => {
      appliedHostFilter.value = hostFilter.value
      appliedPackFilter.value = packFilter.value
      appliedAuthorFilter.value = authorFilter.value
      currentPage.value = 1
      const datesChanged = dateFrom.value !== appliedDateFrom.value || dateTo.value !== appliedDateTo.value
      if (datesChanged) {
        fetchData()
      }
    }

    const resetFilters = () => {
      dateFrom.value = ''
      dateTo.value = ''
      hostFilter.value = null
      packFilter.value = null
      authorFilter.value = null
      appliedHostFilter.value = null
      appliedPackFilter.value = null
      appliedAuthorFilter.value = null
      currentPage.value = 1
      const datesChanged = appliedDateFrom.value !== '' || appliedDateTo.value !== ''
      if (datesChanged) {
        fetchData()
      } else {
        appliedDateFrom.value = ''
        appliedDateTo.value = ''
      }
    }

    const truncate = (str, len) => {
      if (!str) return ''
      return str.length > len ? `${str.substring(0, len)}...` : str
    }

    // Средние оценки по отфильтрованным строкам
    const calcAvg = (arr, field) => {
      const vals = arr.map(r => r[field]).filter(v => v != null)
      if (!vals.length) return '—'
      return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(2)
    }

    const averages = computed(() => ({
      game: calcAvg(filteredRows.value, 'gameRating'),
      host: calcAvg(filteredRows.value, 'hostRating'),
      pack: calcAvg(filteredRows.value, 'packRating'),
    }))

    // Копирование непустых комментариев в буфер обмена
    const copyComments = () => {
      const comments = filteredRows.value
        .map(r => r.comment)
        .filter(Boolean)
        .join('\n')
      if (!comments) return
      navigator.clipboard.writeText(comments)
    }

    // Начальная загрузка
    fetchData()

    return {
      dateFrom,
      dateTo,
      hostFilter,
      packFilter,
      authorFilter,
      searchTerm,
      currentPage,
      isLoading,
      datePickerConfig,
      hostOptions,
      packOptions,
      authorOptions,
      filteredRows,
      columns,
      weeklyData,
      hostData,
      packData,
      authorData,
      weeklySeries,
      weeklyChartOptions,
      hostSeries,
      hostChartOptions,
      packSeries,
      packChartOptions,
      authorSeries,
      authorChartOptions,
      averages,
      copyComments,
      applyFilters,
      resetFilters,
      truncate,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
